import { MonthPicker } from '@/components/ui/monthpicker'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  getCurrentSalesOrderCalendarMonth,
  getSalesOrderCalendarMonthLabel,
  getShiftedSalesOrderCalendarMonth,
} from '@/lib/services/sales-orders/sales-order.schema'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { useState, type ReactElement } from 'react'
import { ButtonGroup, ButtonGroupButton } from '../patterns/button-group'
import { Button } from '../ui/button'

export function SalesOrdersCalendarMonthPicker({
  month,
  onMonthChange,
}: {
  month?: string | null
  onMonthChange?: (month: string) => void
}): ReactElement {
  const [open, setOpen] = useState(false)
  const currentMonth = getCurrentSalesOrderCalendarMonth()
  const selectedMonth = month ?? currentMonth
  const monthLabel = getSalesOrderCalendarMonthLabel(selectedMonth)

  const handleMonthChange = (nextMonth: string) => {
    onMonthChange?.(nextMonth)
  }

  const handlePickerMonthSelect = (date: Date) => {
    handleMonthChange(getCurrentSalesOrderCalendarMonth(date))
    setOpen(false)
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-9 whitespace-nowrap"
        onClick={() => handleMonthChange(currentMonth)}
      >
        This month
      </Button>
      <ButtonGroup className="h-9" aria-label="Calendar month navigation">
        <ButtonGroupButton
          type="button"
          size="icon-sm"
          aria-label="Previous month"
          onClick={() => handleMonthChange(getShiftedSalesOrderCalendarMonth(selectedMonth, -1))}
        >
          <ChevronLeft className="size-4" />
        </ButtonGroupButton>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <ButtonGroupButton
              type="button"
              divider
              className="min-w-[10.5rem] px-3"
              aria-label={`Change calendar month, ${monthLabel}`}
            >
              <CalendarDays className="size-4 text-muted-foreground" />
              <span>{monthLabel}</span>
            </ButtonGroupButton>
          </PopoverTrigger>
          <PopoverContent size="small" padding="compact" align="center">
            <MonthPicker
              selectedMonth={`${selectedMonth}-01`}
              onMonthSelect={handlePickerMonthSelect}
            />
          </PopoverContent>
        </Popover>
        <ButtonGroupButton
          type="button"
          size="icon-sm"
          aria-label="Next month"
          onClick={() => handleMonthChange(getShiftedSalesOrderCalendarMonth(selectedMonth, 1))}
        >
          <ChevronRight className="size-4" />
        </ButtonGroupButton>
      </ButtonGroup>
    </div>
  )
}
